import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';

const anchorNav = [
  { id: 'intro', label: '网站介绍' },
  { id: 'contact', label: '联系方式' },
  { id: 'terms', label: '使用须知' },
  { id: 'privacy', label: '隐私政策' },
];

const milestones = [
  { year: '起步', title: '聚焦娱乐与热点', desc: '以娱乐八卦与网络热议为切口，建立清晰的分类频道。' },
  { year: '成长', title: '多分类资讯网络', desc: '扩展到社会、明星动态等频道，覆盖更丰富的热点内容。' },
  { year: '现在', title: '优化阅读体验', desc: '打磨筛选、搜索与推荐，让热点资讯更快被看懂。' },
];

const notes = [
  '本站为演示站点，所有资讯内容均为示例数据，仅供产品效果展示，请勿作为事实依据。',
  '页面中的收藏功能仅保存在你的浏览器本地，用于演示，不会上传或同步到任何服务器。',
  '分享功能为前端演示，点击后不会真实发布到第三方社交平台。',
  '本站不提供登录、支付、下载或内容付费服务，也不包含任何真实音频、视频播放能力。',
];

export default function About() {
  useSeo({
    title: '关于我们 - 吃瓜网热点新闻平台',
    description:
      '了解吃瓜网的网站定位、发展历程、联系方式与使用须知。吃瓜网聚焦热点新闻、娱乐八卦、明星动态与社会事件资讯。',
    keywords: '吃瓜网,关于我们,热点新闻,娱乐八卦,联系方式',
    canonicalPath: '/about',
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 py-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          <header className="mt-5 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-information-line"></i>
              关于吃瓜网
            </span>
            <h1 className="mt-4 text-3xl md:text-4xl font-bold font-heading text-foreground-950">
              让每一条热点都被快速看懂
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              吃瓜网是一家聚焦最新热点新闻、娱乐八卦和社会事件资讯的平台。我们用清晰的分类、克制的排版与舒适的阅读体验，
              帮助用户快速获取并理解正在发生的热点。
            </p>
          </header>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <nav className="lg:col-span-3">
              <div className="lg:sticky lg:top-28 rounded-lg border border-background-200 bg-background-50 p-3">
                <p className="px-3 py-2 text-xs font-medium text-foreground-400 uppercase tracking-wide">页面导航</p>
                <ul className="flex flex-col">
                  {anchorNav.map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="block px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </nav>

            <div className="lg:col-span-9 flex flex-col gap-10">
              <section id="intro" className="scroll-mt-28">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">网站定位与发展</h2>
                <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">
                  吃瓜网的目标用户是喜欢及时获取明星动态、网络热议话题和社会热点的年轻及中年互联网用户。
                  网站旨在快速、精准地传达热点内容，同时满足用户追踪新闻时效与多维度信息的深度需求。
                </p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {milestones.map((m) => (
                    <div key={m.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                      <span className="inline-flex items-center rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-700 whitespace-nowrap">
                        {m.year}
                      </span>
                      <h3 className="mt-3 text-base font-semibold text-foreground-950">{m.title}</h3>
                      <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed">{m.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
                  <h3 className="text-base font-semibold text-foreground-950">内容说明</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    本站当前处于演示阶段，页面中的所有资讯、标题与图片均为示例内容，用于展示产品效果，
                    不代表任何真实事件或人物。我们会持续完善内容结构。
                  </p>
                </div>
              </section>

              <section id="contact" className="scroll-mt-28">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">联系方式</h2>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <h3 className="mt-3 text-sm font-semibold text-foreground-950">邮箱联系</h3>
                    <p className="mt-1.5 text-sm text-foreground-600 break-all">contact@chigua.example.com</p>
                    <p className="mt-1 text-xs text-foreground-400">工作日 9:00 - 18:00 回复</p>
                  </div>

                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className="w-10 h-10 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center">
                      <i className="ri-wechat-line text-lg"></i>
                    </span>
                    <h3 className="mt-3 text-sm font-semibold text-foreground-950">微信公众号</h3>
                    <div className="mt-3 w-28 h-28 rounded-md overflow-hidden bg-background-100 border border-background-200">
                      <img
                        src="https://readdy.ai/api/search-image?query=minimal-black-white-qr-code-on-white-card-flat-lay&width=300&height=300&seq=about-qr-wechat&orientation=squarish"
                        alt="吃瓜网微信公众号二维码示意图"
                        title="吃瓜网微信公众号二维码 - 吃瓜网联系方式"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="mt-2 text-xs text-foreground-400">扫码关注（演示示意）</p>
                  </div>

                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                      <i className="ri-weibo-line text-lg"></i>
                    </span>
                    <h3 className="mt-3 text-sm font-semibold text-foreground-950">官方微博</h3>
                    <div className="mt-3 w-28 h-28 rounded-md overflow-hidden bg-background-100 border border-background-200">
                      <img
                        src="https://readdy.ai/api/search-image?query=minimal-black-white-qr-code-on-light-gray-card-top-view&width=300&height=300&seq=about-qr-weibo&orientation=squarish"
                        alt="吃瓜网官方微博二维码示意图"
                        title="吃瓜网官方微博二维码 - 吃瓜网联系方式"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="mt-2 text-xs text-foreground-400">扫码关注（演示示意）</p>
                  </div>
                </div>
              </section>

              <section id="terms" className="scroll-mt-28">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">使用须知</h2>
                <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 flex flex-col gap-3">
                  {notes.map((t) => (
                    <p key={t} className="flex items-start gap-2 text-sm text-foreground-700 leading-relaxed">
                      <i className="ri-checkbox-circle-line text-primary-500 mt-0.5"></i>
                      <span>{t}</span>
                    </p>
                  ))}
                </div>
              </section>

              <section id="privacy" className="scroll-mt-28">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">隐私政策</h2>
                <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <p className="text-sm text-foreground-700 leading-loose">
                    我们重视你的隐私。本站不收集账号信息，也不会将你的浏览数据用于商业用途。
                    当前版本中，仅“收藏”功能会在你的设备本地写入少量数据（收藏的资讯标识），
                    你可以随时通过清除浏览器数据将其删除。若你通过邮箱与我们联系，
                    你所提供的联系方式仅用于回复你的问题，不会对外提供。后续如引入统计或账号功能，
                    我们会在页面上明确告知并更新本政策。
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-primary-500 text-primary-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      返回首页
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                    <a
                      href="mailto:contact@chigua.example.com"
                      className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-background-300 bg-background-50 text-foreground-800 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-mail-send-line"></i>
                      邮件联系
                    </a>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}